/**
 * Once-per-tab auth identity boundary watcher (#866).
 *
 * Two problems this closes:
 * 1. LOCAL identity boundaries not covered by explicit logout / refresh
 *    rejection: a session refetch that comes back null (confirmed
 *    unauthentication), an A→B user switch via setQueryData (Telegram verify /
 *    admin login while already signed in), and a fresh login (null→A). Each
 *    must wipe the FULL cache + CSRF state so no private query (favorites,
 *    profile, account-info with staleTime: Infinity, ...) survives into the
 *    next identity, then broadcast so sibling tabs do the same.
 * 2. SIBLING-initiated boundaries: this tab receives a purge broadcast from
 *    another tab's logout / refresh rejection / identity change and wipes its
 *    own cache.
 *
 * Mounted exactly ONCE per tab (see __root.tsx AppProviders). A per-useSession
 * implementation would run this effect once per hook caller and amplify
 * broadcasts N-fold.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { broadcastAuthPurge, purgeAuthState, subscribeAuthPurge } from '@/lib/auth/authLifecycle';
import type { UserSession } from '@/lib/server-fns/auth-fns';
import { useSession } from './useAuthQuery';

export function useAuthIdentitySync(): void {
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const seenKnownIdentityRef = useRef(false);
  const lastKnownUserIdRef = useRef<string | null | undefined>(undefined);

  // Local identity boundaries. session === undefined (loading / transient
  // failure) is deliberately NOT a boundary: a blip must never throw away
  // valid cached data, and AuthUnavailableError must not purge.
  useEffect(() => {
    const knownUserId = session === undefined ? undefined : (session?.id ?? null);
    if (knownUserId === undefined) return;
    if (seenKnownIdentityRef.current && lastKnownUserIdRef.current === knownUserId) return;

    if (seenKnownIdentityRef.current) {
      purgeAuthState(queryClient);
      // Restore the (new) session synchronously so an A→B switch never flashes
      // unauthenticated and the guard doesn't bounce to /login mid-switch.
      if (session) queryClient.setQueryData<UserSession>(['auth', 'session'], session);
      broadcastAuthPurge();
    }
    seenKnownIdentityRef.current = true;
    lastKnownUserIdRef.current = knownUserId;
  }, [session, queryClient]);

  // Sibling-initiated boundaries. No broadcast here — echoing would ping-pong
  // between tabs; the origin tab already told everyone.
  useEffect(() => subscribeAuthPurge(() => purgeAuthState(queryClient)), [queryClient]);
}
